import { GenericHeaderNotification } from 'uicomponents';

import { getAgoraRTC, getVirtualBackgroundExtension } from './agoraLoader';
import {
  getDefaultVideoQuality,
  handleVideoQualities,
  setVideoQualityToTrack,
  videoMetaMap
} from './agoraVideoQuality.service';
import { DenoiserLevel } from './denoiser/constants';
import { getDenoiserHook } from './denoiser/denoiserFactory';
import DialogService from './dialogs/DialogService';
import { getValueFromLocalstore, saveValueToLocalstore } from './localStorage.service';
import loggerService from './logger.service';
import { retry } from './utils';

import type {
  CameraVideoTrackInitConfig,
  ICameraVideoTrack,
  ILocalAudioTrack,
  ILocalTrack,
  ILocalVideoTrack,
  IMicrophoneAudioTrack,
  MicrophoneAudioTrackInitConfig,
  ScreenVideoTrackInitConfig,
  VideoEncoderConfiguration,
  AIDenoiserProcessorLevel
} from './agora.types';

import { AGORA, getStaticAssetsUrl, LOCAL_STORAGE_KEYS } from '@/constants';
import i18n from '@/i18n';
import NotificationService from '@/services/dialogs/notifications';
import { isAnyMediaRoomReconnecting, MediaRoom } from '@/services/liveMedia.service';
import { showToast, ToastTypes } from '@/services/toast.service';
import store from '@/store';
import { User } from '@/store/modules/event/types';
import { getCapitalizeText } from '@/utils/stringsUtil';

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 500;

export const trackMap = new Map<string, ILocalVideoTrack>();
export enum LocalTrackStatus {
  CREATING = 'CREATING',
  CREATED = 'CREATED',
  PUBLISHING = 'PUBLISHING',
  PUBLISHED = 'PUBLISHED',
  UNPUBLISHING = 'UNPUBLISHING',
  UNPUBLISHED = 'UNPUBLISHED'
}

export enum MediaDeviceStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

const PRESET_TO_CONFIG: Record<string, { width: number; height: number; frameRate: number }> = {
  '180p_1': { width: 320, height: 180, frameRate: 15 },
  '240p_4': { width: 424, height: 240, frameRate: 15 },
  '360p_9': { width: 640, height: 360, frameRate: 15 },
  '480p_8': { width: 848, height: 480, frameRate: 15 },
  '720p_1': { width: 1280, height: 720, frameRate: 15 },
  '720p_2': { width: 1280, height: 720, frameRate: 30 },
  '1080p_1': { width: 1920, height: 1080, frameRate: 15 },
  '1080p_2': { width: 1920, height: 1080, frameRate: 30 },
  '1080p_3': { width: 1920, height: 1080, frameRate: 30 }
};

export type LocalTrack = {
  trackId?: string;
  track?: ICameraVideoTrack | IMicrophoneAudioTrack;
  deviceId?: string;
  deviceStatus?: MediaDeviceStatus;
  channelName: string;
  mediaTrackType: ILocalTrack['trackMediaType'];
  trackStatus: LocalTrackStatus;
  localDuplicateTrack?: ICameraVideoTrack;
  isDuplicateTrackLive?: boolean;
};

export type AudioTrackSnapshot = {
  trackId?: string;
  trackStatus: LocalTrackStatus;
  volumeLevel: number;
  readyState?: string;
};

type Stream = {
  id: string;
  user: User;
  videoTrack: ICameraVideoTrack;
  audioTrack: IMicrophoneAudioTrack;
  loading: boolean;
  isLocal: boolean;
};

let localAudioLevelTrackingInterval: NodeJS.Timeout;

let processor: any = null;
// Track that currently holds the VB processor — must call unpipe() on this object, not the incoming track.
let processorSourceTrack: ILocalVideoTrack | null = null;
// Per-track processors for local recording duplicates.
const trackProcessors = new Map<string, any>();
const MAX_TRACK_PROCESSORS = 10;
let noiseCancellationProcessor: any = null;
// Track that currently holds the denoiser processor — same reason as processorSourceTrack above.
let denoiserSourceTrack: IMicrophoneAudioTrack | null = null;
let denoiserOverloaded = false;

// Stops AI Denoiser from rebuilding over and over when track-update events
// fire rapidly (the "rebuild storm" issue, GS-29631):
//   denoiserRebuildInProgress   — a rebuild is currently running; later callers wait on it
//   denoiserRebuildStartedAt    — timestamp when the last rebuild kicked off (used for logging)
//   denoiserRebuildFinishedAt   — timestamp when the last rebuild completed (gates reuse window)
//   DENOISER_REUSE_WINDOW_MS    — within this window, reuse the existing processor
let denoiserRebuildCount = 0;
let denoiserRebuildStartedAt = 0;
let denoiserRebuildFinishedAt = 0;
let denoiserRebuildInProgress: Promise<any | null> | null = null;
let denoiserOverloadCount = 0;
const MAX_OVERLOADS = 2;
const DENOISER_REUSE_WINDOW_MS = 2000;

// Add a function to clean up processors
export async function cleanupProcessors() {
  // Clean up all track processors
  for (const [trackId, processor] of trackProcessors.entries()) {
    try {
      await processor.disable();
    } catch (e) {
      loggerService.error(`Error disabling processor for track ${trackId}`, e);
    }
  }
  trackProcessors.clear();

  // Reset denoiser state
  await cleanupNoiseCancellation();
}

export async function getCustomTrackProcessor(videoTrack: ILocalVideoTrack): Promise<any | null> {
  const trackId = videoTrack.getTrackId();
  let processor = trackProcessors.get(trackId);
  if (!processor && videoTrack) {
    // Get the lazily loaded extension
    const extension = getVirtualBackgroundExtension();
    if (!extension) {
      loggerService.error('Virtual background extension not loaded');
      return null;
    }

    // Prevent memory leak: clean up oldest processor if at max capacity
    if (trackProcessors.size >= MAX_TRACK_PROCESSORS) {
      const oldestTrackId = trackProcessors.keys().next().value;
      if (oldestTrackId) {
        await cleanupProcessorForTrack(oldestTrackId);
        loggerService.warn(`Cleaned up oldest processor to prevent memory leak: ${oldestTrackId}`);
      }
    }

    // Create a new VirtualBackgroundProcessor instance for this track
    processor = extension.createProcessor();
    try {
      // Initialize the extension and pass in the URL of the Wasm file
      await processor.init(`${getStaticAssetsUrl()}agora/agora-wasm.wasm`);
      trackProcessors.set(trackId, processor);
    } catch (e) {
      loggerService.error('Fail to load WASM resource!', e);
      return null;
    }
  }
  if (processor) {
    try {
      // Always unpipe first to avoid conflicts
      await videoTrack.unpipe();
      videoTrack.pipe(processor).pipe(videoTrack.processorDestination).enable();
    } catch (e) {
      loggerService.error('Fail to pipe processor to custom video track!', e);
    }
  }
  return processor;
}

// Add function to clean up processor for a specific track
export async function cleanupProcessorForTrack(trackId: string) {
  const processor = trackProcessors.get(trackId);
  if (processor) {
    try {
      await processor.disable();
    } catch (e) {
      loggerService.error(`Error disabling processor for track ${trackId}`, e);
    }
    trackProcessors.delete(trackId);
  }
}

async function getProcessorInstance(videoTrack: ILocalVideoTrack) {
  const currentBackgroundFilter = store.state.mediaroom.currentBackgroundFilter;

  if (!processor && videoTrack) {
    // Get the lazily loaded extension
    const extension = getVirtualBackgroundExtension();
    if (!extension) {
      loggerService.error('Virtual background extension not loaded');
      return null;
    }
    // Create a VirtualBackgroundProcessor instance
    processor = extension.createProcessor();

    try {
      // Initialize the extension and pass in the URL of the Wasm file
      await processor.init(`${getStaticAssetsUrl()}agora/agora-wasm.wasm`);
    } catch (e) {
      loggerService.error('Fail to load WASM resource!');
      return null;
    }
    // Inject the extension into the video processing pipeline in the SDK
  }
  const track = trackMap.get(videoTrack.getTrackId()) || videoTrack;
  // Captured up front so the catch below can still report it after the reference is reset.
  const previousSourceId = processorSourceTrack?.getTrackId() ?? null;

  try {
    // Hand the processor back before re-piping it. Agora frees the processor only inside
    // the unpipe() of the track that took it, so unpiping the incoming `track` achieves
    // nothing while an older track object still holds the processor — a recreated camera
    // track, or a stage transition — and pipe() below then throws "already piped" on this
    // and every later call. pipe() unpipes the incoming track on our behalf.
    if (processorSourceTrack && processorSourceTrack !== track) {
      loggerService.warn('[stream.service] Releasing virtual background processor from its previous track', {
        previousTrackId: previousSourceId,
        nextTrackId: track.getTrackId()
      });
      processorSourceTrack.unpipe();
    }
    if (processor) {
      try {
        await processor.unpipe();
      } catch (e) {
        loggerService.error('Fail to unpipe processor!', e);
      }
    }
    track.pipe(processor).pipe(track.processorDestination);
    processorSourceTrack = track;
  } catch (e) {
    loggerService.error('Fail to pipe processor to video track!', e, {
      trackId: track.getTrackId(),
      previousTrackId: previousSourceId,
      isSameTrack: previousSourceId === track.getTrackId(),
      hasBackgroundFilter: Boolean(currentBackgroundFilter)
    });
    // Drop the wedged processor so the next call builds a fresh one, which holds no source
    // binding, rather than failing for the rest of the session.
    processor = null;
    processorSourceTrack = null;
    if (!currentBackgroundFilter) {
      return null;
    }
    NotificationService.toast(
      'Virtual Background Error',
      'Failed to apply virtual background. Try using a different camera or browser.',
      'error',
      null
    );
  }

  return processor;
}

export async function setBackgroundBlurring(videoTrack: ICameraVideoTrack) {
  if (videoTrack) {
    let processor: any = null;
    if (store.state.event.event.is_local_recording) {
      processor = await getCustomTrackProcessor(videoTrack);
    } else {
      processor = await getProcessorInstance(videoTrack);
    }

    if (processor) {
      try {
        processor.setOptions({ type: 'blur', blurDegree: 2 });
        await processor.enable();
      } catch (e) {
        loggerService.error("Couldn't enable background blur", e);
        NotificationService.toast(
          'Background Blur Error',
          'Failed to enable background blur. Please try again.',
          'error',
          null
        );
      }
    } else {
      loggerService.warn('Virtual background processor not available for blur');
      NotificationService.toast(
        'Virtual Background Unavailable',
        'Virtual background feature is still loading. Please try again in a moment.',
        'warning',
        null
      );
    }
  }
}

export async function unsetBackgroundFilter(videoTrack: ICameraVideoTrack) {
  let processor: any = null;

  if (store.state.event.event.is_local_recording) {
    processor = await getCustomTrackProcessor(videoTrack);
  } else {
    processor = await getProcessorInstance(videoTrack);
  }

  if (processor) {
    try {
      await processor.disable();
    } catch (e) {
      loggerService.error("Couldn't disable background filter", e);
    }
  }
}

export async function setBackgroundImage(videoTrack: ICameraVideoTrack, imgURL?: string) {
  const imgElement = document.createElement('img');

  imgElement.onload = async () => {
    let processor: any = null;
    if (store.state.event.event.is_local_recording) {
      processor = await getCustomTrackProcessor(videoTrack);
    } else {
      processor = await getProcessorInstance(videoTrack);
    }

    if (processor) {
      try {
        processor.setOptions({ type: 'img', source: imgElement });
        await processor.enable();
      } catch (e) {
        loggerService.error("Couldn't enable background image", e);
        NotificationService.toast(
          'Background Image Error',
          'Failed to apply background image. Please try again.',
          'error',
          null
        );
      }
    } else {
      loggerService.warn('Virtual background processor not available for image');
      NotificationService.toast(
        'Virtual Background Unavailable',
        'Virtual background feature is still loading. Please try again in a moment.',
        'warning',
        null
      );
    }
  };

  const getDataURL: any = (url: string) => {
    return fetch(url)
      .then(response => response.blob())
      .then(
        blob =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .catch(err => {
        loggerService.warn(`Problem in fetching ${url}! Retrying in 5sec`, err);
      });
  };

  getDataURL(imgURL).then((dataUrl: string) => {
    imgElement.src = dataUrl;
  });
}

export async function closeTracks(tracks: ILocalTrack[]) {
  tracks?.forEach(async track => {
    try {
      const trackId = track?.getTrackId();
      const videoMeta = videoMetaMap.get(trackId);
      if (videoMeta) {
        clearInterval(videoMeta.interval);
        videoMetaMap.delete(trackId);
      }
      // Clean up processor for this specific track
      if (trackId && store.state.event.event.is_local_recording) {
        await cleanupProcessorForTrack(trackId);
      }
      track?.close();
    } catch (error) {
      loggerService.error('Could not close track', error, track);
    }
  });
}

const LOCAL_RECORDING_MIN_WIDTH_ENCODINGS = [
  { min: 1920, label: '1080p_3' },
  { min: 1280, label: '720p_2' },
  { min: 640, label: '360p_4' },
  { min: 320, label: '180p_1' },
  { min: 0, label: '120p_1' }
] as const;

export type EncodingLabel = (typeof LOCAL_RECORDING_MIN_WIDTH_ENCODINGS)[number]['label'];

export function getEncodingBasedOnWidth(width: number): EncodingLabel {
  const w = Number.isFinite(width) ? Math.max(0, Math.floor(width)) : 0;
  for (const e of LOCAL_RECORDING_MIN_WIDTH_ENCODINGS) {
    if (w >= e.min) {
      return e.label;
    }
  }
  // If no encoding matches, return the lowest quality
  loggerService.warn(`No encoding found for width ${width}, defaulting to 120p_1`);
  return '120p_1';
}

export async function createLocalVideoTrack(
  config: CameraVideoTrackInitConfig,
  channelName: string
): Promise<ICameraVideoTrack> {
  const AgoraRTC = await getAgoraRTC();
  const defaultQuality = getDefaultVideoQuality(channelName);
  let encoderConfig: VideoEncoderConfiguration | string = defaultQuality;
  if (store.state.event.bitrateCap > 0) {
    const presetConfig = PRESET_TO_CONFIG[defaultQuality];

    if (presetConfig) {
      encoderConfig = {
        width: presetConfig.width,
        height: presetConfig.height,
        bitrateMax: store.state.event.bitrateCap
      };
    }

    loggerService.info('[VIDEO QUALITY] Applied bitrate cap', {
      preset: defaultQuality,
      bitrateMax: store.state.event.bitrateCap,
      encoderConfig
    });
  }
  const cameraTrack: ICameraVideoTrack = await AgoraRTC.createCameraVideoTrack({
    encoderConfig,
    ...config
  });
  trackMap.set(cameraTrack.getTrackId(), cameraTrack);
  handleVideoQualities(cameraTrack, channelName, defaultQuality);
  return cameraTrack;
}

export async function createLocalVideoTrackWithCustomQuality(
  config: CameraVideoTrackInitConfig
): Promise<ICameraVideoTrack> {
  const AgoraRTC = await getAgoraRTC();
  return AgoraRTC.createCameraVideoTrack(config);
}

export async function createLocalAudioTrack(config?: MicrophoneAudioTrackInitConfig): Promise<IMicrophoneAudioTrack> {
  const AgoraRTC = await getAgoraRTC();
  return AgoraRTC.createMicrophoneAudioTrack(config);
}

export function getAudioTrackSnapshots(channelName: string): AudioTrackSnapshot[] {
  return ((store.state.mediaroom.localTracks ?? []) as LocalTrack[])
    .filter(t => t.channelName === channelName && t.mediaTrackType === 'audio')
    .map(t => ({
      trackId: t.trackId,
      trackStatus: t.trackStatus,
      volumeLevel: t.track ? (t.track as ILocalAudioTrack).getVolumeLevel() : 0,
      readyState: t.track?.getMediaStreamTrack()?.readyState
    }));
}

export function hasPublishedAudioTrack(channelName: string): boolean {
  return ((store.state.mediaroom.localTracks ?? []) as LocalTrack[]).some(
    t => t.channelName === channelName && t.mediaTrackType === 'audio' && t.trackStatus === LocalTrackStatus.PUBLISHED
  );
}

export async function createVolumeMonitoringTrack(deviceId?: string): Promise<IMicrophoneAudioTrack | undefined> {
  try {
    const AgoraRTC = await getAgoraRTC();
    const microphoneId = deviceId || store.state.mediaroom.currentDevices?.microphone?.deviceId;
    const volumeTrack = await AgoraRTC.createMicrophoneAudioTrack({
      microphoneId
    });

    loggerService.info('Created new audio track for volume monitoring', {
      trackId: volumeTrack.getTrackId(),
      deviceId: microphoneId
    });

    return volumeTrack;
  } catch (error) {
    loggerService.error('Failed to create volume monitoring audio track', error);
    return undefined;
  }
}

const audioLevelCallbacks = new Set();
export function registerAudioLevelCallback(cb: any) {
  audioLevelCallbacks.add(cb);

  if (audioLevelCallbacks.size >= 10) {
    loggerService.warn(`Register Audio level Callbacks size reached to: ${audioLevelCallbacks.size}`);
  }
}
export function unregisterAudioLevelCallback(cb: any) {
  audioLevelCallbacks.delete(cb);
}

let volumeMonitoringTrack: IMicrophoneAudioTrack | undefined = undefined;

const startTrackingVolume = async (track: ILocalAudioTrack, volumeThreshold: number, dialogTimeout: number) => {
  loggerService.info('localAudioLevelTracking: removing', localAudioLevelTrackingInterval);
  clearInterval(localAudioLevelTrackingInterval);
  localAudioLevelTrackingInterval = setInterval(() => {
    const volumeLevel = Math.floor(track?.getVolumeLevel() * 100);

    audioLevelCallbacks.forEach((cb: any) => cb(volumeLevel));
    const isReconnecting = isAnyMediaRoomReconnecting();
    if (
      volumeLevel > volumeThreshold &&
      store.state.mediaroom.showMuteNotifications &&
      !isReconnecting &&
      store.state.mediaroom?.signalMetadata?.strength > 2
    ) {
      DialogService.show(
        GenericHeaderNotification,
        {
          text: i18n.global.t(
            '@@translate@@ It seems that you are speaking on mute. Click on Mic icon to unmute yourself'
          )
        },
        {
          hideOverlay: true,
          cancelOnClickOutside: false,
          blockBackgroundActions: false,
          closeOnCTAClick: false,
          autoCancelTimeout: dialogTimeout
        }
      );
      store.commit('mediaroom/updateMuteNotificationState', false, {
        root: true
      });
    }
  }, 200);
  loggerService.info('localAudioLevelTracking: adding', localAudioLevelTrackingInterval);
};

export async function toggleAudioLevelTracking(status: 'on' | 'off') {
  if (volumeMonitoringTrack) {
    volumeMonitoringTrack.close();
    volumeMonitoringTrack = undefined;
  }
  const { localTracks } = store.state.mediaroom;

  const audioTrack = localTracks.find((track: LocalTrack) => {
    return track.mediaTrackType === 'audio';
  });

  if (!audioTrack?.track) {
    return;
  }

  if (status === 'on') {
    // Create a new audio track for volume monitoring if it doesn't exist
    const isMainStage = !audioTrack.channelName?.includes(AGORA.UID_SUFFIX.BACKSTAGE);
    if (!volumeMonitoringTrack && store.state.event.event.is_local_recording && isMainStage) {
      const deviceId = audioTrack.track.getMediaStreamTrack().getSettings().deviceId;
      volumeMonitoringTrack = await createVolumeMonitoringTrack(deviceId);
      if (volumeMonitoringTrack) {
        volumeMonitoringTrack.setMuted(false);
        startTrackingVolume(volumeMonitoringTrack, 65, 5000);
        return;
      }
    }

    startTrackingVolume(
      audioTrack.track as ILocalAudioTrack,
      50,
      store.state.event.event.is_local_recording ? 5000 : 3000
    );
  }

  if (status === 'off') {
    clearInterval(localAudioLevelTrackingInterval);
    if (volumeMonitoringTrack) {
      (volumeMonitoringTrack as IMicrophoneAudioTrack).close();
      volumeMonitoringTrack = undefined;
    }
  }
}

export function switchVideoQuality(videoTrack: ICameraVideoTrack, quality: any) {
  const useFullHD = store.getters['event/useFullHD'];

  // Prevent switching to 1080p if Full HD is not enabled
  if (quality?.name === 'ULTRA_HIGH' && !useFullHD) {
    loggerService.warn('[VIDEO QUALITY] Attempted to switch to 1080p but Full HD not enabled');
    // Fallback to 720p
    const fallbackQuality = AGORA.VIDEO_QUALITY.find(vq => vq.name === 'HIGH') || AGORA.VIDEO_QUALITY[0];
    setVideoQualityToTrack(videoTrack, fallbackQuality);
    return;
  }

  setVideoQualityToTrack(videoTrack, quality);
}
export async function createLocalScreenShareTracks(channelName: MediaRoom['channelName']): Promise<ILocalTrack[]> {
  const AgoraRTC = await getAgoraRTC();
  const encoderConfig =
    store.state.event.useOptimizeRSCloudRecording && store.state.event.event.is_local_recording
      ? {
          width: {
            max: 1280,
            min: 720
          }
        }
      : {
          width: {
            max: 1920,
            min: 1366
          }
        };
  const screenVideoTrackInitConfig: ScreenVideoTrackInitConfig = {
    encoderConfig
  };

  let tracks: ILocalTrack | ILocalTrack[] = await AgoraRTC.createScreenVideoTrack(screenVideoTrackInitConfig, 'auto');

  if (!Array.isArray(tracks)) {
    tracks = [tracks];
  }

  const videoTrack = tracks[0];

  videoTrack.on('track-ended', () => {
    loggerService.error('Track Ended Received for screen share');
    showToast({
      message: i18n.global.t(
        "@@translate@@ Your screen share stopped. If you didn't stop it, please restart the share."
      ),
      type: ToastTypes.WARNING,
      dismissible: true
    });
    store.dispatch('mediaroom/stopScreenSharing', {
      channelName
    });
  });

  return tracks;
}

export async function getCamaraDevices(skipPermissionCheck?: undefined | false | true): Promise<MediaDeviceInfo[]> {
  const AgoraRTC = await getAgoraRTC();
  return AgoraRTC.getCameras(skipPermissionCheck);
}

export async function getMicrophoneDevices(skipPermissionCheck?: undefined | false | true): Promise<MediaDeviceInfo[]> {
  const AgoraRTC = await getAgoraRTC();
  return AgoraRTC.getMicrophones(skipPermissionCheck);
}

export async function getSpeakerDevices(skipPermissionCheck?: undefined | false | true): Promise<MediaDeviceInfo[]> {
  const AgoraRTC = await getAgoraRTC();
  return AgoraRTC.getPlaybackDevices(skipPermissionCheck);
}

async function setDevicesAfterDeviceChange(status: String, deviceTag: 'camera' | 'microphone' | 'speaker') {
  try {
    const oldCurrentDevice = {
      ...store.state.mediaroom.currentDevices?.[deviceTag]
    } as MediaDeviceInfo | null | undefined;

    await store.dispatch('mediaroom/initializeIndividualDevice', deviceTag);

    const newCurrentDevice = {
      ...store.state.mediaroom.currentDevices?.[deviceTag]
    } as MediaDeviceInfo | null | undefined;

    if (newCurrentDevice?.label !== oldCurrentDevice?.label) {
      NotificationService.toast(
        `${getCapitalizeText(deviceTag)} Changed`,
        `Your ${deviceTag} device has changed. Now ${newCurrentDevice?.label} will be used.`,
        'success',
        null,
        AGORA.GENERAL_NOTIFICATION_TIMEOUT
      );
    }
  } catch (error) {
    loggerService.warn(
      `Something went wrong while fetching the ${deviceTag} after device change. Disconnected Device status ${status}`,
      error
    );
  }
}

export async function subscribeDeviceEvents() {
  const AgoraRTC = await getAgoraRTC();
  AgoraRTC.onCameraChanged = async info => {
    loggerService.info('Camera device changed', { state: info.state, deviceKind: info.device?.kind });
    setDevicesAfterDeviceChange(info.state, 'camera');
  };
  AgoraRTC.onMicrophoneChanged = async info => {
    loggerService.info('Microphone device changed', { state: info.state, deviceKind: info.device?.kind });
    setDevicesAfterDeviceChange(info.state, 'microphone');
  };
  AgoraRTC.onPlaybackDeviceChanged = async info => {
    loggerService.info('Speaker device changed', { state: info.state, deviceKind: info.device?.kind });
    setDevicesAfterDeviceChange(info.state, 'speaker');
  };
}

export function setMediaDevice(deviceId: string, track: IMicrophoneAudioTrack | ICameraVideoTrack): Promise<void> {
  return track.setDevice(deviceId);
}

export function getStreamsSortedByVolumeLevel(
  streams: Stream[],
  channelId: string,
  forceActiveSpeaker: boolean = false
): Stream[] {
  // const isMobile = window.mobileCheck && window.mobileCheck();
  if (streams.length < 2) {
    return streams;
  }

  const threshold = 3;

  if (!forceActiveSpeaker && streams.length <= threshold) {
    return streams;
  }

  try {
    const { activeSpeakerId } = store.state.mediaroom.mediaRooms[channelId];

    if (!activeSpeakerId) {
      return streams;
    }

    const activeStream = streams.find(stream => {
      return stream.id === activeSpeakerId;
    });

    if (activeStream) {
      streams = [activeStream, ...streams.filter(s => s.id !== activeStream.id)];
    }
  } catch (e) {
    loggerService.error('error in getStreamsSortedByVolumeLevel', e);
  }

  return streams;
}

function setupDenoiserOverloadHandler(processor: any) {
  if (!processor) {
    return;
  }

  const denoiser = getDenoiserHook();
  denoiser.setupOverloadHandler(processor, async (elapsedMs?: number) => {
    try {
      if (denoiserOverloaded) {
        return;
      }
      denoiserOverloaded = true;
      denoiserOverloadCount++;

      loggerService.warn(
        `[Denoiser] overload (${elapsedMs ?? 'unknown'} ms). Disabling noise cancellation. Overload ${denoiserOverloadCount}/${MAX_OVERLOADS}.`
      );

      await processor.disable();
      store.commit('mediaroom/setNoiseCancellation', false, { root: true });
      saveValueToLocalstore(LOCAL_STORAGE_KEYS.noiseCancellationEnabled, false);

      if (denoiserOverloadCount >= MAX_OVERLOADS) {
        NotificationService.toast(
          '@@translate@@ Noise cancellation has been disabled due to repeated system overload. Please refresh the page.',
          '',
          'error'
        );
      } else {
        NotificationService.toast(
          '@@translate@@ Noise cancellation was turned off due to high system load to keep your audio clear.',
          '',
          'warning'
        );
      }
    } catch (error) {
      loggerService.error('[Denoiser] Error handling AI Denoiser overload', error);
    }
  });
}

async function getProcessorInstanceForDeNoiser(): Promise<any | null> {
  // Fast path: processor exists and is healthy — return immediately.
  if (noiseCancellationProcessor && !denoiserOverloaded) {
    setupDenoiserOverloadHandler(noiseCancellationProcessor);
    return noiseCancellationProcessor;
  }

  // --- From here on we are in a recovery or first-creation path ---

  // Permanent failure: max overloads exhausted.
  if (denoiserOverloaded && denoiserOverloadCount >= MAX_OVERLOADS) {
    loggerService.error('[Denoiser] Max overloads reached — keeping processor disabled');
    return null;
  }

  // Clear overload flag before recovery
  if (denoiserOverloaded) {
    loggerService.warn('[Denoiser] Clearing overload flag for recovery attempt');
    denoiserOverloaded = false;
  }

  // Create processor if it doesn't exist
  if (!noiseCancellationProcessor) {
    const denoiser = getDenoiserHook();
    try {
      noiseCancellationProcessor = await retry(
        () => Promise.resolve(denoiser.createProcessor()),
        MAX_RETRY_ATTEMPTS,
        RETRY_DELAY_MS
      );
      denoiserOverloaded = false;
      setupDenoiserOverloadHandler(noiseCancellationProcessor);
    } catch (e) {
      loggerService.error('Fail to create processor for AI Denoiser!', e);
      NotificationService.toast(
        'Facing issue with noise cancellation, please refresh the page or rejoin.',
        '',
        'error'
      );
      store.commit('mediaroom/setNoiseCancellationCompatible', false, {
        root: true
      });
      return null;
    }
  }

  return noiseCancellationProcessor;
}

async function getProcessorInstanceForPiping(): Promise<any | null> {
  // If a rebuild is already running, wait for that one instead of starting another.
  if (denoiserRebuildInProgress) {
    loggerService.warn('[Denoiser][Rebuild] another rebuild already running — waiting for it');
    return denoiserRebuildInProgress;
  }

  const now = Date.now();
  const msSinceLastRebuild = denoiserRebuildFinishedAt ? now - denoiserRebuildFinishedAt : -1;

  // If we just finished a rebuild a moment ago, reuse the existing processor
  // instead of building yet another one. The caller will re-pipe its track to it.
  if (noiseCancellationProcessor && msSinceLastRebuild >= 0 && msSinceLastRebuild < DENOISER_REUSE_WINDOW_MS) {
    loggerService.info(
      `[Denoiser] Reusing processor (last pipe ${msSinceLastRebuild}ms ago, within ${DENOISER_REUSE_WINDOW_MS}ms window)`
    );
    await getDenoiserHook().detachProcessor(noiseCancellationProcessor);
    return noiseCancellationProcessor;
  }

  denoiserRebuildInProgress = (async () => {
    denoiserRebuildCount += 1;
    const msSinceLast = denoiserRebuildStartedAt ? now - denoiserRebuildStartedAt : -1;
    denoiserRebuildStartedAt = now;

    const denoiser = getDenoiserHook();

    if (noiseCancellationProcessor) {
      // Detach (disable + unpipe) before deciding reuse vs rebuild.
      await denoiser.detachProcessor(noiseCancellationProcessor);

      // If the processor is healthy (no overload), reuse it.
      // Both v1 and v2 can safely re-pipe a healthy processor to a new track.
      if (!denoiserOverloaded) {
        loggerService.info(
          `[Denoiser] Re-piping existing healthy processor (count=${denoiserRebuildCount} msSinceLast=${msSinceLast})`
        );
        denoiserRebuildFinishedAt = Date.now();
        return noiseCancellationProcessor;
      }

      // Overloaded processor: v1 can't destroy (no API) — forced to reuse anyway.
      // v2 has destroy() — tear down and build fresh for a clean pipeline.
      if (!denoiser.canRebuildProcessor()) {
        loggerService.warn('[Denoiser][Rebuild] processor overloaded but v1 cannot rebuild — reusing');
        denoiserRebuildFinishedAt = Date.now();
        return noiseCancellationProcessor;
      }

      loggerService.warn(
        `[Denoiser][Rebuild] destroying overloaded processor (count=${denoiserRebuildCount} msSinceLast=${msSinceLast})`
      );
      await denoiser.destroyProcessor(noiseCancellationProcessor);
      noiseCancellationProcessor = null;
    } else {
      loggerService.info(`[Denoiser] Creating processor (count=${denoiserRebuildCount} msSinceLast=${msSinceLast})`);
    }

    try {
      noiseCancellationProcessor = await retry(
        () => Promise.resolve(denoiser.createProcessor()),
        MAX_RETRY_ATTEMPTS,
        RETRY_DELAY_MS
      );
      // Fresh processor — clear overload state so the permanent-failure gate
      // in getProcessorInstanceForDeNoiser doesn't block this new instance.
      denoiserOverloaded = false;
      denoiserOverloadCount = 0;
      setupDenoiserOverloadHandler(noiseCancellationProcessor);
      denoiserRebuildFinishedAt = Date.now();
    } catch (e) {
      loggerService.error('Fail to create processor for AI Denoiser!', e);
      NotificationService.toast(
        '[Denoiser] Facing issue with noise cancellation, please refresh the page or rejoin.',
        '',
        'error'
      );
      store.commit('mediaroom/setNoiseCancellationCompatible', false, {
        root: true
      });
      return null;
    }

    return noiseCancellationProcessor;
  })();

  try {
    return await denoiserRebuildInProgress;
  } finally {
    denoiserRebuildInProgress = null;
  }
}

export async function initializeNoiseCancellation(audioTrack: IMicrophoneAudioTrack) {
  const deiNoiserProcessor = await getProcessorInstanceForPiping();
  if (!deiNoiserProcessor) {
    return;
  }
  try {
    await retry(
      async () => {
        if (denoiserSourceTrack && denoiserSourceTrack !== audioTrack) {
          denoiserSourceTrack.unpipe();
        }
        audioTrack.pipe(deiNoiserProcessor).pipe(audioTrack.processorDestination);
      },
      MAX_RETRY_ATTEMPTS,
      RETRY_DELAY_MS
    );
    denoiserSourceTrack = audioTrack;
    // Apply post-pipe configuration. v2's setLatency requires the processor to
    // be piped first; v1 is a no-op.
    const denoiser = getDenoiserHook();
    await denoiser.configureAfterPipe(deiNoiserProcessor);
  } catch (e) {
    loggerService.error('Fail to pipe processor to audio track after retries!', e);
    NotificationService.toast('Facing issue with noise cancellation, please refresh the page or rejoin.', '', 'error');
    return;
  }

  await toggleNoiseCancellation(getValueFromLocalstore(LOCAL_STORAGE_KEYS.noiseCancellationEnabled, true));
  setNoiseCancellationLevel(getValueFromLocalstore(LOCAL_STORAGE_KEYS.noiseCancellationLevel, DenoiserLevel.SOFT));
}

/**
 * Checks if AI Denoiser is compatible with the current browser.
 * This is now async as it requires the Agora SDK to be loaded first.
 */
export async function isDeNoiserCompatible(): Promise<boolean> {
  const denoiser = getDenoiserHook();
  const isCompatible = denoiser.checkCompatibility();
  if (!isCompatible) {
    loggerService.error('Does not support AI Denoiser!');
    return false;
  }
  return isCompatible;
}

/**
 * Synchronous version that returns the current known state.
 * Returns true if SDK isn't loaded yet (optimistic), false if known incompatible.
 */
export function isDeNoiserCompatibleSync(): boolean {
  const denoiser = getDenoiserHook();
  return denoiser.checkCompatibility();
}

export const toggleNoiseCancellation = async (status: boolean) => {
  const deiNoiserProcessor = await getProcessorInstanceForDeNoiser();
  if (!deiNoiserProcessor) {
    return;
  }
  if (status) {
    denoiserOverloaded = false;
    await deiNoiserProcessor.enable();
  } else {
    await deiNoiserProcessor.disable();
  }
};

export const setNoiseCancellationLevel = async (level: AIDenoiserProcessorLevel) => {
  const deiNoiserProcessor = await getProcessorInstanceForDeNoiser();
  if (!deiNoiserProcessor) {
    return;
  }
  await deiNoiserProcessor.setLevel(level);
};

/**
 * Resets the per-session overload + rebuild bookkeeping so the next room
 * starts from a clean slate. Does NOT touch the processor reference itself.
 */
function resetDenoiserSessionState(): void {
  denoiserOverloaded = false;
  denoiserOverloadCount = 0;
  denoiserRebuildCount = 0;
  denoiserRebuildStartedAt = 0;
  denoiserRebuildFinishedAt = 0;
}

/**
 * Detaches the noise cancellation processor from the current audio track
 * without destroying it. Use on room transitions (backstage ↔ main stage)
 * so the processor can be re-piped to the next track without expensive WASM teardown.
 */
export async function detachNoiseCancellation(): Promise<void> {
  if (!noiseCancellationProcessor) {
    return;
  }
  await getDenoiserHook().detachProcessor(noiseCancellationProcessor);
  resetDenoiserSessionState();
  loggerService.info('[Denoiser] Noise cancellation detached (processor preserved for re-pipe)');
}

/**
 * Cleans up the noise cancellation processor and resets all denoiser state.
 * Call on channel-leave / app-unmount — NOT on device switch or mute toggle.
 * Delegates version-specific teardown to the active denoiser hook.
 */
export async function cleanupNoiseCancellation(): Promise<void> {
  if (!noiseCancellationProcessor) {
    return;
  }
  await getDenoiserHook().destroyProcessor(noiseCancellationProcessor);
  resetDenoiserSessionState();
  noiseCancellationProcessor = null;
  denoiserSourceTrack = null;
  denoiserRebuildInProgress = null;
  loggerService.info('[Denoiser] Noise cancellation cleaned up');
}
